import React from 'react';

import { BillingReportResult } from '../../types';
import { currencyFormatter } from '../../utils/currencyFormatter';

interface Props {
  billingReportResult: BillingReportResult;
}

interface StatusBadgeProps {
  status: string | boolean;
  className?: string;
}

const StatusBadge = ({ status, className = 'text-xs' }: StatusBadgeProps) => {
  if (status === null || status === undefined) {
    return <span>-</span>;
  }

  const normalizedStatus = String(status).toLowerCase().replace(/\s+/g, '_').trim();

  const colorMap: { [key: string]: string } = {
    errors: 'text-red-900 bg-red-200 font-semibold ',
    running: 'text-blue-900 bg-blue-200 font-semibold ',
    submitted: 'text-green-900 bg-green-200 font-semibold ',
    refresh: 'text-green-900 bg-green-200 font-semibold ',
    regenerate: 'text-red-900 bg-red-200 font-semibold ',
    generate: 'text-green-900 bg-green-200 font-semibold ',
    success: 'text-green-900 bg-green-200 font-semibold '
  };

  const colorClasses = colorMap[normalizedStatus] || 'text-gray-600 bg-gray-200';

  const statusComponents = String(status)
    .split('.')
    .map((str) => str.replace(/_/g, ' '))
    .map((str) => str.charAt(0).toUpperCase() + str.slice(1).toLowerCase());

  return (
    <span className={`${colorClasses} py-1 px-3 rounded-full ${className}`}>
      {statusComponents.join(' - ')}
    </span>
  );
};

const formatPercentage = (value: number): string => {
  return `${(value * 100).toFixed(1)}%`;
};

const formatDuration = (startAt: string, endAt: string): string => {
  const start = new Date(startAt);
  const end = new Date(endAt);
  const diffMs = end.getTime() - start.getTime();

  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);

  if (hours > 0) {
    return `${hours}h ${minutes}m ${seconds}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  } else {
    return `${seconds}s`;
  }
};

const formatTimeAgo = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();

  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffMonths = Math.floor(diffDays / 30);
  const diffYears = Math.floor(diffDays / 365);

  if (diffYears > 0) {
    return `${diffYears} year${diffYears === 1 ? '' : 's'} ago`;
  } else if (diffMonths > 0) {
    return `${diffMonths} month${diffMonths === 1 ? '' : 's'} ago`;
  } else if (diffDays > 0) {
    return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  } else if (diffHours > 0) {
    return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  } else if (diffMinutes > 0) {
    return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
  } else {
    return 'just now';
  }
};

const calculateRPS = (result: BillingReportResult): string => {
  if (!result.processed_started_at || !result.processed_completed_at || !result.row_count) {
    return 'N/A';
  }

  const start = new Date(result.processed_started_at);
  const end = new Date(result.processed_completed_at);
  const durationSeconds = (end.getTime() - start.getTime()) / 1000;

  if (durationSeconds <= 0) return 'N/A';

  const rps = result.row_count / durationSeconds;
  return `${rps.toFixed(2)} rows/sec`;
};

const ReportOverview = ({ billingReportResult }: Props) => {
  return (
    <div className="space-y-4">
      {/* Report Details Card */}
      <div className="bg-white rounded shadow mb-4 p-4">
        <div className="flex gap-16">
          <div className="text-gray-700">
            <span className="text-lg font-semibold">Filters</span>
            <div className="mt-2 text-gray-500">
              <ul>
                <li>
                  Date Range: <br />
                  {new Date(billingReportResult.billing_report.range_start).toLocaleString()} -{' '}
                  {new Date(billingReportResult.billing_report.range_end).toLocaleString()}
                </li>

                {billingReportResult.billing_report.jurisdiction ? (
                  <li>
                    Jurisdiction:
                    <br />
                    {billingReportResult.billing_report.jurisdiction}
                  </li>
                ) : null}

                {billingReportResult.billing_report.person_id ? (
                  <li>
                    Person ID: <br /> {billingReportResult.billing_report.person_id}
                  </li>
                ) : null}
              </ul>
            </div>
          </div>
          <div className="text-gray-700">
            <span className="text-lg font-semibold">Type</span>
            <div className="mt-2">
              <StatusBadge status={billingReportResult.type} />
            </div>
          </div>

          <div className="text-gray-700">
            <span className="text-lg font-semibold">Status</span>
            <div className="mt-2">
              <StatusBadge status={billingReportResult.status} />
            </div>
          </div>

          <div className="text-gray-700">
            <span className="text-lg font-semibold">Run Details</span>
            <div className="mt-2">
              <p>
                By: {billingReportResult.processed_by.first_name}{' '}
                {billingReportResult.processed_by.last_name}
              </p>

              <span>
                {new Date(billingReportResult.processed_started_at).toLocaleString()}
                {' - '}
                {new Date(billingReportResult.processed_completed_at).toLocaleString()} (
                {formatTimeAgo(billingReportResult.processed_completed_at)})
              </span>

              <p>
                <span className="text-lg font-semibold">Duration</span>:{' '}
                {formatDuration(
                  billingReportResult.processed_started_at,
                  billingReportResult.processed_completed_at
                )}
              </p>

              <p>
                <span className="text-lg font-semibold">RPS</span>:{' '}
                {calculateRPS(billingReportResult)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white shadow rounded-lg p-6">
          <div className="text-gray-500 font-medium uppercase tracking-wide">Billable Items</div>
          <div className="text-xl font-bold">{billingReportResult.row_count}</div>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <div className="text-gray-500 font-medium uppercase tracking-wide">Items With Errors</div>
          <div className="text-xl font-bold">
            {billingReportResult.error_count} ({formatPercentage(billingReportResult.error_rate)})
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <div className="text-gray-500 font-medium uppercase tracking-wide">Submitted Items</div>
          <div className="text-xl font-bold">
            {billingReportResult.submitted_count} (
            {formatPercentage(billingReportResult.submitted_rate)})
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <div className="text-gray-500 font-medium uppercase tracking-wide">Charged</div>
          <div className="text-xl font-bold">
            {currencyFormatter(billingReportResult.amount_charged_in_cents / 100)}
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <div className="text-gray-500 font-medium uppercase tracking-wide">Claims</div>
          <div className="text-xl font-bold">{billingReportResult.number_of_generated_claims}</div>
        </div>
      </div>
    </div>
  );
};

export default ReportOverview;
